/**
 * Verify-organization-invite page state.
 *
 * The new spec accept flow (UPDATE.md §3.6.8) lives entirely on the server:
 *   POST /api/org/:orgId/team/invite/accept  body: { ownerId, token }
 *
 * The invite email deep-links here with `?orgId=...&ownerId=...&token=...`.
 * This duck just parses the query string and calls the server endpoint —
 * the server is the authoritative gate (token timing-safe compared, TTL
 * checked, dual-storage write, audit log appended).
 */

import { orgTeamAPI } from '../../api';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchOrganizationProfiles } from '../../ducks/organizations.duck';

// ================ Action types ================ //

export const VERIFY_INVITE_REQUEST = 'app/VerifyOrganizationInvitePage/VERIFY_INVITE_REQUEST';
export const VERIFY_INVITE_SUCCESS = 'app/VerifyOrganizationInvitePage/VERIFY_INVITE_SUCCESS';
export const VERIFY_INVITE_ERROR = 'app/VerifyOrganizationInvitePage/VERIFY_INVITE_ERROR';

export const VERIFICATION_INVALID_OR_EXPIRED_ERROR =
  'app/VerifyOrganizationInvitePage/VERIFICATION_INVALID_OR_EXPIRED_ERROR';
export const INVALID_PARAMS_ERROR = 'app/VerifyOrganizationInvitePage/INVALID_PARAMS_ERROR';

// ================ Reducer ================ //

const initialState = {
  verifyInviteInProgress: false,
  verifyInviteError: null,
  verificationInvalidOrExpiredError: null,
  invalidParamsError: null,
  isVerified: false,
};

export default function VerifyOrganizationInvitePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case VERIFY_INVITE_REQUEST:
      return {
        ...state,
        verifyInviteInProgress: true,
        verifyInviteError: null,
        verificationInvalidOrExpiredError: null,
        invalidParamsError: null,
        isVerified: false,
      };
    case VERIFY_INVITE_SUCCESS:
      return {
        ...state,
        verifyInviteInProgress: false,
        verifyInviteError: null,
        isVerified: true,
      };
    case VERIFY_INVITE_ERROR:
      return {
        ...state,
        verifyInviteInProgress: false,
        verifyInviteError: payload,
        isVerified: false,
      };

    case VERIFICATION_INVALID_OR_EXPIRED_ERROR:
      return {
        ...state,
        verifyInviteInProgress: false,
        verificationInvalidOrExpiredError: payload,
      };
    case INVALID_PARAMS_ERROR:
      return {
        ...state,
        verifyInviteInProgress: false,
        invalidParamsError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const verifyInviteRequest = () => ({ type: VERIFY_INVITE_REQUEST });
export const verifyInviteSuccess = () => ({ type: VERIFY_INVITE_SUCCESS });
export const verifyInviteError = e => ({
  type: VERIFY_INVITE_ERROR,
  error: true,
  payload: e,
});

export const verificationInvalidOrExpiredError = e => ({
  type: VERIFICATION_INVALID_OR_EXPIRED_ERROR,
  error: true,
  payload: e,
});
export const invalidParamsError = e => ({
  type: INVALID_PARAMS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

/**
 * Accept an org invite using the spec endpoint.
 *
 * URL params (from the invite email): orgId, ownerId, token.
 * Server response codes we map:
 *   404 invite_not_found      → invalid/expired (don't reveal which)
 *   400 invite_token_invalid  → invalid/expired
 *   410 invite_expired        → invalid/expired
 *   409 already_member        → treat as success (idempotent)
 */
export const verifyInvite = urlParams => async dispatch => {
  dispatch(verifyInviteRequest());

  const { orgId, ownerId, token } = urlParams || {};

  if (!orgId || !ownerId || !token) {
    const err = { message: 'Missing orgId, ownerId, or token' };
    dispatch(invalidParamsError(err));
    throw new Error(err.message);
  }

  try {
    await orgTeamAPI.acceptInvite(orgId, { ownerId, token });

    // Refresh the user (so privateData.memberOfOrganizations is in state)
    // and the org switcher.
    await dispatch(fetchCurrentUser());
    await dispatch(fetchOrganizationProfiles());

    return dispatch(verifyInviteSuccess());
  } catch (error) {
    const status = error?.status || error?.response?.status;
    if (status === 404 || status === 400 || status === 410) {
      dispatch(
        verificationInvalidOrExpiredError({
          message: 'This invite link is invalid or has expired.',
        })
      );
    } else if (status === 409) {
      // Already a member — treat as success so the user gets dropped at the
      // org dashboard rather than seeing an error.
      await dispatch(fetchCurrentUser());
      await dispatch(fetchOrganizationProfiles());
      return dispatch(verifyInviteSuccess());
    } else {
      dispatch(verifyInviteError(storableError(error)));
    }
    throw error;
  }
};

export const loadData = (params, search) => (dispatch, getState) => {
  // Skip the accept call if no session cookie yet — the component will
  // redirect unauthenticated users to /signup with a `from` carrying this
  // URL, and loadData will re-run once they're authenticated and land back
  // here.
  const isAuthenticated = !!getState().Auth?.isAuthenticated;
  if (!isAuthenticated) return Promise.resolve(null);

  const urlParams = parse(search);
  return dispatch(verifyInvite(urlParams));
};

// The whole page is the visitor's own: the server (no signed-in user) loads nothing, and the
// browser accepts the invite after server rendering (see onlyClientDataNeeded in src/Routes.js).
export const loadClientData = loadData;
